import { Tenant, DEFAULT_TENANT_BRANDING, DEFAULT_TENANT_SETTINGS } from '../types/tenant';

/**
 * Utility functions for tenant/workspace management
 * 
 * Examples:
 * - nissan.{APP_DOMAIN} -> 'nissan'
 * - johns-auto-shop.{APP_DOMAIN} -> 'johns-auto-shop'
 * - localhost:8080?tenant=acme -> 'acme'
 * - {APP_DOMAIN} -> null (central auth/default)
 */

// Get the base domain from environment variable
const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN;

// Helper to detect if we're in production
const isProduction = () => {
  // Check if we're on a production domain or if Vite is in production mode
  const hostname = window.location.hostname;
  const isProductionDomain = hostname === 'www.wattfactory.tech' || 
                            hostname === 'wattfactory.tech' ||
                            hostname.includes('vercel.app');
  
  return isProductionDomain || import.meta.env.PROD;
};

/**
 * Extract tenant slug from current URL
 * Handles all environments: localhost, staging, and production
 * - localhost:8080?tenant=acme -> 'acme'
 * - acme.staging.wattfactory.tech -> 'acme'
 * - acme.wattfactory.tech -> 'acme'
 * - custom.domain.com (with verified custom domain) -> slug from database
 */
export const getCurrentTenantSlug = (): string | null => {
  const hostname = window.location.hostname;
  const params = new URLSearchParams(window.location.search);
  
  // Development mode - check for ?tenant= parameter
  if (hostname === 'localhost') {
    const tenantParam = params.get('tenant');
    console.log('🔍 Development mode - tenant param:', tenantParam);
    return tenantParam;
  }
  
  // Staging environment - extract from subdomain
  if (hostname === 'staging.wattfactory.tech' || hostname === 'www.staging.wattfactory.tech') {
    return null; // These are central domains
  }
  
  // Check for staging subdomains (e.g., acme.staging.wattfactory.tech)
  if (hostname.includes('.staging.wattfactory.tech')) {
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const potentialSlug = parts[0];
      if (potentialSlug !== 'www') {
        return potentialSlug;
      }
    }
  }
  
  // Production environment - extract from subdomain
  const baseWithWww = APP_DOMAIN.startsWith('www.') ? APP_DOMAIN : `www.${APP_DOMAIN}`;
  const baseWithoutWww = APP_DOMAIN.replace('www.', '');
  
  // Check if it's the central domain (with or without www)
  if (hostname === baseWithWww || hostname === baseWithoutWww || hostname === APP_DOMAIN) {
    return null;
  }
  
  // Extract subdomain (e.g., 'workspace' from 'workspace.wattfactory.tech')
  if (hostname.includes('.')) {
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      const potentialSlug = parts[0];
      // Make sure it's not 'www'
      if (potentialSlug !== 'www') {
        return potentialSlug;
      }
    }
  }
  
  return null;
};

/**
 * Check if current domain is the central domain
 * Handles all environments: localhost, staging, and production
 */
export const isCentralDomain = (): boolean => {
  const hostname = window.location.hostname;
  
  // Development environment (localhost)
  if (hostname === 'localhost') {
    const tenantSlug = getCurrentTenantSlug();
    // If there's a tenant parameter, it's not central
    if (tenantSlug) {
      return false;
    }
    return true; // localhost without tenant param is central
  }
  
  // Staging environment (staging.wattfactory.tech)
  if (hostname === 'staging.wattfactory.tech' || hostname === 'www.staging.wattfactory.tech') {
    return true; // These are central domains in staging
  }
  
  // Production environment (wattfactory.tech)
  const baseWithWww = APP_DOMAIN.startsWith('www.') ? APP_DOMAIN : `www.${APP_DOMAIN}`;
  const baseWithoutWww = APP_DOMAIN.replace('www.', '');
  return hostname === baseWithWww || hostname === baseWithoutWww || hostname === APP_DOMAIN;
};

/**
 * Get workspace URL for a given slug
 * Handles all environments: localhost, staging, and production
 */
export const getWorkspaceUrl = (slug: string, path: string = ''): string => {
  const hostname = window.location.hostname;
  // Normalize path and safely append tenant param in dev
  const ensureLeadingSlash = (p: string) => (p && !p.startsWith('/') ? `/${p}` : p);
  const normalizedPath = ensureLeadingSlash(path || '/');
  const withTenantParam = (p: string) => {
    // If path already has a query string, append with &; otherwise with ?
    const separator = p.includes('?') ? '&' : '?';
    return `${p}${separator}tenant=${slug}`;
  };
  
  // Development environment (localhost)
  if (hostname === 'localhost') {
    return `http://localhost:8080${withTenantParam(normalizedPath)}`;
  }
  
  // Staging environment (staging.wattfactory.tech or any *.staging.wattfactory.tech)
  if (
    hostname === 'staging.wattfactory.tech' ||
    hostname === 'www.staging.wattfactory.tech' ||
    hostname.endsWith('.staging.wattfactory.tech')
  ) {
    return `https://${slug}.staging.wattfactory.tech${normalizedPath}`;
  }
  
  // Production environment (wattfactory.tech)
  return `https://${slug}.wattfactory.tech${normalizedPath}`;
};

/**
 * Get workspace URL respecting custom domains
 * Handles all environments: localhost, staging, and production
 */
export const getWorkspaceUrlFromObject = (workspace: { slug: string; domain?: string; domain_verified?: boolean }, path: string = ''): string => {
  const hostname = window.location.hostname;
  const ensureLeadingSlash = (p: string) => (p && !p.startsWith('/') ? `/${p}` : p);
  const normalizedPath = ensureLeadingSlash(path || '/');
  const withTenantParam = (p: string) => {
    const separator = p.includes('?') ? '&' : '?';
    return `${p}${separator}tenant=${workspace.slug}`;
  };
  
  // Development environment (localhost)
  if (hostname === 'localhost') {
    return `http://localhost:8080${withTenantParam(normalizedPath)}`;
  }
  
  // Use custom domain if verified
  if (workspace.domain && workspace.domain_verified) {
    return `https://${workspace.domain}${normalizedPath}`;
  }
  
  // Staging environment (staging.wattfactory.tech or any *.staging.wattfactory.tech)
  if (
    hostname === 'staging.wattfactory.tech' ||
    hostname === 'www.staging.wattfactory.tech' ||
    hostname.endsWith('.staging.wattfactory.tech')
  ) {
    return `https://${workspace.slug}.staging.wattfactory.tech${normalizedPath}`;
  }
  
  // Production environment (wattfactory.tech)
  return `https://${workspace.slug}.wattfactory.tech${normalizedPath}`;
};

/**
 * Get central domain URL
 * Handles all environments: localhost, staging, and production
 */
export const getCentralDomainUrl = (path: string = ''): string => {
  const hostname = window.location.hostname;
  
  // Development environment (localhost)
  if (hostname === 'localhost') {
    return `http://localhost:8080${path}`;
  }
  
  // Staging environment (staging.wattfactory.tech)
  if (
    hostname === 'staging.wattfactory.tech' ||
    hostname === 'www.staging.wattfactory.tech' ||
    hostname.endsWith('.staging.wattfactory.tech')
  ) {
    return `https://staging.wattfactory.tech${path}`;
  }
  
  // Production environment (wattfactory.tech)
  return `https://www.wattfactory.tech${path}`;
};

/**
 * Get central auth URL
 * Handles all environments: localhost, staging, and production
 */
export const getCentralAuthUrl = (returnTo?: string): string => {
  const hostname = window.location.hostname;
  
  // Development environment (localhost)
  if (hostname === 'localhost') {
    const baseUrl = 'http://localhost:8080';
    if (returnTo) {
      return `${baseUrl}/auth?returnTo=${encodeURIComponent(returnTo)}`;
    }
    return `${baseUrl}/auth`;
  }
  
  // Staging environment (staging.wattfactory.tech)
  if (
    hostname === 'staging.wattfactory.tech' ||
    hostname === 'www.staging.wattfactory.tech' ||
    hostname.endsWith('.staging.wattfactory.tech')
  ) {
    const baseUrl = 'https://staging.wattfactory.tech';
    if (returnTo) {
      return `${baseUrl}/auth?returnTo=${encodeURIComponent(returnTo)}`;
    }
    return `${baseUrl}/auth`;
  }
  
  // Production environment (wattfactory.tech)
  const baseUrl = 'https://www.wattfactory.tech';
  if (returnTo) {
    return `${baseUrl}/auth?returnTo=${encodeURIComponent(returnTo)}`;
  }
  return `${baseUrl}/auth`;
};

/**
 * Get tenant slug from URL parameters (fallback method)
 * Example: wattos.com?tenant=nissan
 */
export const extractTenantFromParams = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('tenant');
};

/**
 * Check if the application is running in multi-tenant mode
 */
export const isMultiTenantMode = (): boolean => {
  return getCurrentTenantSlug() !== null;
};

/**
 * Generate tenant-specific storage paths
 */
export const getTenantStoragePath = (tenantSlug: string | null, path: string): string => {
  if (!tenantSlug) {
    return `default/${path}`;
  }
  return `${tenantSlug}/${path}`;
};

/**
 * Apply tenant branding to CSS custom properties
 */
export const applyTenantBranding = (branding: Tenant['branding']): void => {
  const root = document.documentElement;
  
  root.style.setProperty('--tenant-primary', branding.primaryColor);
  root.style.setProperty('--tenant-secondary', branding.secondaryColor);
  root.style.setProperty('--tenant-accent', branding.accentColor);
  
  // Update document title and favicon
  document.title = `${branding.companyName} - Manufacturing Quality Management`;
  
  if (branding.favicon) {
    updateFavicon(branding.favicon);
  }
};

/**
 * Update favicon dynamically
 */
const updateFavicon = (faviconUrl: string): void => {
  const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
  link.type = 'image/x-icon';
  link.rel = 'shortcut icon';
  link.href = faviconUrl;
  document.getElementsByTagName('head')[0].appendChild(link);
};

/**
 * Create a default tenant object for fallback
 */
export const createDefaultTenant = (): Tenant => {
  return {
    id: 'default',
    slug: 'default',
    name: 'Default Workspace',
    branding: DEFAULT_TENANT_BRANDING,
    settings: DEFAULT_TENANT_SETTINGS,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

/**
 * Validate workspace slug format (V3 requirements)
 * - Max 20 characters
 * - Alphanumeric + hyphens only (a-z, 0-9, -)
 * - Cannot start or end with hyphen
 */
export const isValidWorkspaceSlug = (slug: string): boolean => {
  // V3 requirements: 20 chars max, alphanumeric + hyphens
  const slugRegex = /^[a-z0-9][a-z0-9-]{0,18}[a-z0-9]$|^[a-z0-9]$/;
  return slugRegex.test(slug) && slug.length <= 20;
};

/**
 * Validate tenant slug format (legacy support)
 */
export const isValidTenantSlug = (slug: string): boolean => {
  // Legacy: Allow alphanumeric characters and hyphens, 3-50 characters
  const slugRegex = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;
  return slugRegex.test(slug);
};

/**
 * Generate tenant-aware API endpoints
 */
export const getTenantApiEndpoint = (tenantSlug: string | null, endpoint: string): string => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  
  if (!tenantSlug) {
    return `${baseUrl}/api/${endpoint}`;
  }
  
  return `${baseUrl}/api/tenants/${tenantSlug}/${endpoint}`;
};

/**
 * Debug logging for tenant detection (development only)
 */
export const debugTenantDetection = (): void => {
  // Only log in development and when specifically needed
  if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_TENANT) {
    // console.group('🏢 Tenant Detection Debug');
    // console.log('Hostname:', window.location.hostname);
    // console.log('Extracted tenant:', getCurrentTenantSlug());
    // console.log('Multi-tenant mode:', isMultiTenantMode());
    // console.groupEnd();
  }
}; 